/**
 * =============================================================================
 *  THANH CONG CU TREN
 * =============================================================================
 *
 *  Chi hien nhung gi that su lam duoc: nut nao khong hoat dong duoc voi loi
 *  hien tai (hoac voi lua chon hien tai) thi bi vo hieu hoa VA noi ro ly do khi
 *  ro chuot, chu khong hien ra roi bao loi sau khi bam.
 *
 *  BO CUC, theo dung yeu cau cua nguoi dung:
 *
 *    [Chọn] [Sửa chữ] [Chèn ▾]        <- hai che do dung nhieu nhat o ngoai,
 *                                        moi thu SINH RA doi tuong vao mot menu
 *    [⟲ Xoay trái] [⟳ Xoay phải] [Xóa] [Trang ▾]
 *                                     <- thao tac tren TRANG DANG CHON o thanh
 *                                        ben; nhom quan ly tep trang vao mot menu
 *
 *  HAI DIEU KIEN KHONG DUOC PHA:
 *
 *  1. MOT CONG CU VAO MENU KHONG DUOC MAT PHIM TAT. Bang phim tat va danh sach
 *     muc menu sinh ra tu CUNG MOT nguon (ui/toolbarMenus.ts), va co test doi
 *     chieu hai chieu. Xem ghi chu dau tep do.
 *
 *  2. CONG CU DANG BAT PHAI NHIN THAY DUOC KE CA KHI NO NAM TRONG MENU. Nut mo
 *     menu "Chèn" doi nhan va bieu tuong theo muc dang bat. Nguoi dung khong
 *     thay minh dang cam cai gi se bam len trang de chon va bat ngo ve ra mot
 *     hinh chu nhat.
 */
import { useRef } from 'react';
import { store, useAppState } from '../state';
import { INSPECTOR_TRIGGER_ATTR } from './InspectorPopover';
import { DropdownMenu, type MenuItemSpec } from './DropdownMenu';
import {
  DIRECT_TOOLS,
  INSERT_ITEMS,
  activeInsertItem,
  hintWithShortcut,
  shortcutLabel,
  type ToolSpec,
} from './toolbarMenus';
import type { PageOps } from './usePageOps';

export interface ToolbarProps {
  onOpenFile: (file: File) => void;
  onGoToPage: (pageIndex: number) => void;
  onSave: () => void;
  /** Loi hien tai co ghi duoc tep khong. Quyet dinh nut Luu va cong cu "Sửa chữ". */
  writeCapable: boolean;
  saving: boolean;
  /** So lan sua chu dang cho xuat - hien ngay tren nut de Luu co nghia ro rang. */
  pendingTextEdits: number;

  /**
   * Nguoi dung da chon mot tep anh. Thanh cong cu KHONG doc tep va KHONG kiem
   * tra gi ca - no chi chuyen tiep. Moi phep kiem (giai ma that, do gia bang
   * byte, de nghi giam mau) nam trong core/annotations/images.ts, va no phai
   * nam DUNG mot cho: mot phep kiem so bo o day se cho ra hai luat khac nhau.
   */
  onPickImage: (file: File) => void;
  /** Loi hien tai ghi nhan duoc byte anh khong. Quyet dinh muc "Chèn ảnh". */
  imageCapable: boolean;
  /**
   * Da co mot anh cho san, dang doi nguoi dung dat len trang.
   *
   * Khac `tool === 'image'`: cong cu co the dang bat trong khi anh chua ghi
   * nhan xong. Muc menu sang len chi khi THAT SU co gi de dat.
   */
  imageArmed: boolean;
  /** Ten tep anh dang cho - de muc menu noi ro no se dat cai gi. */
  imageLabel: string | null;

  /**
   * Thao tac trang. CUNG MOT ban ma thanh ben dung (xem ui/usePageOps.ts):
   * nut "Xóa" o day phai xoa dung nhung trang dang sang len o thanh ben, va
   * cach duy nhat bao dam dieu do la khong co ban thu hai nao ton tai.
   */
  pageOps: PageOps;
}

export function Toolbar({
  onOpenFile,
  onGoToPage,
  onSave,
  writeCapable,
  saving,
  pendingTextEdits,
  onPickImage,
  imageCapable,
  imageArmed,
  imageLabel,
  pageOps,
}: ToolbarProps) {
  const state = useAppState();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const mergeInputRef = useRef<HTMLInputElement | null>(null);
  const ready = state.status === 'ready' && state.info !== null;
  /**
   * SO TRANG LAY TU SO DO TRANG, khong tu tep da mo luc dau.
   *
   * Day chinh la cho loi cu lo ra ro nhat: ghep them mot tep vao thi thanh ben
   * hien 4 trang con o day van ghi "/ 3". Bo dem va danh sach trang phai doc tu
   * DUNG MOT nguon, neu khong thi mot trong hai dang noi doi.
   */
  const pageCount = state.pagePlan.length || (state.info?.pageCount ?? 0);

  // Chu thich lay tu KHO TRANG THAI chu khong nhan qua prop: day la cung mot
  // mang ma bang doi tuong va lop hien thi doc, nen ba cho khong the lech nhau.
  const annotationCount = state.annotations.length;
  const pendingChanges = pendingTextEdits + annotationCount;
  const changeSummary = [
    pendingTextEdits > 0 ? `${pendingTextEdits} lần sửa chữ` : '',
    annotationCount > 0 ? `${annotationCount} chú thích` : '',
  ]
    .filter(Boolean)
    .join(' và ');

  // --- Menu "Chèn" ---------------------------------------------------------

  const armedInsert = activeInsertItem(state.tool);
  const insertItems: MenuItemSpec[] = INSERT_ITEMS.map((item, i) => {
    const previousGroup = i > 0 ? INSERT_ITEMS[i - 1]?.group : item.group;
    const isImage = item.id === 'image';
    // "Sửa chữ" can duong ghi; cac cong cu chen thi khong - chung chi tao chu
    // thich. Rieng anh can loi ghi nhan duoc byte anh.
    const blocked = isImage && !imageCapable;
    return {
      key: item.id,
      label: item.label,
      icon: item.icon,
      shortcut: shortcutLabel(item),
      separatorBefore: item.group !== previousGroup,
      disabled: !ready || blocked,
      checked: isImage ? imageArmed : state.tool === item.id,
      title: blocked
        ? 'Lõi hiện tại không nhúng được ảnh'
        : isImage && imageArmed
          ? `Đang chờ đặt “${imageLabel ?? 'ảnh'}” — kéo một khung trên trang, hoặc bấm một cái để đặt ở cỡ tự nhiên. Chọn lại để đổi tệp khác.`
          : hintWithShortcut(item),
      onSelect: () => {
        /*
          CHÈN ẢNH - duong day du, tu day den /Stamp trong tep xuat ra.

          `accept` chi la goi y cho hop thoai cua he dieu hanh, KHONG phai mot
          phep kiem. Nguoi dung van chon duoc "anh.png" von la mot tep van ban
          doi ten, va moi he dieu hanh cho phep chon "tat ca cac tep". Phep kiem
          that la registerImage(): no chay chinh bo giai ma cua duong ghi tren
          byte that. Xem core/annotations/images.ts.

          Muc menu mo lai duoc hop thoai ke ca khi da co anh cho san: doi y chon
          tep khac la chuyen binh thuong, va bat nguoi dung dat anh sai roi xoa
          di moi chon lai duoc la mot cai bay.
        */
        if (isImage) imageInputRef.current?.click();
        else store.setTool(item.id);
      },
    };
  });

  // --- Menu "Trang" --------------------------------------------------------

  const extractGate = pageOps.gate('extract');
  const insertBlankGate = pageOps.gate('insertBlank');
  const splitGate = pageOps.gate('split');
  const mergeGate = pageOps.gate('merge');
  const rotateGate = pageOps.gate('rotate');
  const deleteGate = pageOps.gate('delete');

  const pageItems: MenuItemSpec[] = [
    {
      key: 'insertBlank',
      icon: '＋',
      label: 'Chèn trang trắng',
      disabled: insertBlankGate.disabled,
      title: insertBlankGate.reason,
      onSelect: pageOps.insertBlank,
    },
    {
      key: 'extract',
      icon: '⇱',
      label: 'Trích xuất trang đang chọn',
      disabled: extractGate.disabled,
      title: extractGate.reason,
      separatorBefore: true,
      onSelect: pageOps.extract,
    },
    {
      key: 'split',
      icon: '✂',
      label: 'Tách từng trang',
      disabled: splitGate.disabled,
      title: splitGate.reason,
      onSelect: pageOps.splitAll,
    },
    {
      key: 'merge',
      icon: '⧉',
      label: 'Ghép thêm tệp PDF…',
      disabled: mergeGate.disabled,
      title: mergeGate.reason,
      onSelect: () => mergeInputRef.current?.click(),
    },
  ];

  return (
    <header className="toolbar">
      <div className="toolbarGroup">
        <button
          type="button"
          className="iconButton"
          onClick={() => store.toggleSidebar()}
          title={state.sidebarOpen ? 'Ẩn thanh bên' : 'Hiện thanh bên'}
          aria-pressed={state.sidebarOpen}
        >
          ☰
        </button>
        <div className="brand">
          <span className="brandMark">PDF</span>
          <span className="brandName">Editor</span>
        </div>
      </div>

      <div className="toolbarDivider" />

      <div className="toolbarGroup">
        <button type="button" className="button primary" onClick={() => inputRef.current?.click()}>
          Mở tệp
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf,.pdf"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onOpenFile(file);
            e.target.value = '';
          }}
        />
      </div>

      <div className="toolbarDivider" />

      <div className="toolbarGroup" role="group" aria-label="Công cụ">
        {DIRECT_TOOLS.map((tool) => (
          <ToolButton key={tool.id} tool={tool} ready={ready} writeCapable={writeCapable} />
        ))}

        <DropdownMenu
          label="Chèn"
          icon="＋"
          disabled={!ready}
          title="Thêm hộp chữ, ghi chú, hình khối, ảnh hoặc nét vẽ tay lên trang"
          activeItem={
            armedInsert ? { label: armedInsert.label, icon: armedInsert.icon } : null
          }
          items={insertItems}
        />
        <input
          ref={imageInputRef}
          type="file"
          accept="image/png,image/jpeg,.png,.jpg,.jpeg"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onPickImage(file);
            // Xoa gia tri de chon LAI DUNG TEP DO van phat ra su kien. Khong
            // lam thi nguoi dung huy o buoc xac nhan giam mau se khong chon
            // lai duoc chinh tep vua roi.
            e.target.value = '';
          }}
        />
      </div>

      <div className="toolbarDivider" />

      {/*
        THAO TAC TRANG. Chung tac dong len TRANG DANG CHON o thanh ben, va do la
        ly do ba nut nay phai noi ro ly do khi bi khoa: mot nut xoay im lang vi
        chua chon trang nao doc ra y het mot nut xoay bi hong.
      */}
      <div className="toolbarGroup" role="group" aria-label="Thao tác trang">
        <button
          type="button"
          className="toolButton"
          disabled={rotateGate.disabled}
          title={rotateGate.disabled ? rotateGate.reason : `⟲ ${rotateGate.reason}`}
          onClick={() => pageOps.rotate(270)}
        >
          <span className="toolIcon" aria-hidden="true">⟲</span>
          <span className="toolLabel">Xoay trái</span>
        </button>
        <button
          type="button"
          className="toolButton"
          disabled={rotateGate.disabled}
          title={rotateGate.disabled ? rotateGate.reason : `⟳ ${rotateGate.reason}`}
          onClick={() => pageOps.rotate(90)}
        >
          <span className="toolIcon" aria-hidden="true">⟳</span>
          <span className="toolLabel">Xoay phải</span>
        </button>
        <button
          type="button"
          className="toolButton danger"
          disabled={deleteGate.disabled}
          title={deleteGate.reason}
          onClick={pageOps.remove}
        >
          <span className="toolIcon" aria-hidden="true">🗑</span>
          <span className="toolLabel">Xóa trang</span>
        </button>

        <DropdownMenu
          label="Trang"
          icon="▤"
          disabled={!ready}
          title="Chèn trang trắng, trích xuất, tách hoặc ghép tệp PDF"
          items={pageItems}
          minWidth={248}
        />
        <input
          ref={mergeInputRef}
          type="file"
          accept="application/pdf,.pdf"
          multiple
          hidden
          onChange={(e) => {
            const files = Array.from(e.target.files ?? []);
            // Xoa gia tri de chon LAI dung tep vua roi van kich hoat onChange.
            e.target.value = '';
            if (files.length > 0) pageOps.pickMergeFiles(files);
          }}
        />
      </div>

      <div className="toolbarDivider" />

      <div className="toolbarGroup">
        <button
          type="button"
          className="iconButton"
          disabled={!store.canUndo()}
          onClick={() => store.undo()}
          title="Hoàn tác (Ctrl+Z)"
        >
          ↶
        </button>
        <button
          type="button"
          className="iconButton"
          disabled={!store.canRedo()}
          onClick={() => store.redo()}
          title="Làm lại (Ctrl+Shift+Z)"
        >
          ↷
        </button>
      </div>

      <div className="toolbarSpacer" />

      <div className="toolbarGroup pageNav">
        <button
          type="button"
          className="iconButton"
          disabled={!ready || state.currentPage <= 0}
          onClick={() => onGoToPage(state.currentPage - 1)}
          title="Trang trước"
        >
          ↑
        </button>
        <label className="pageInput">
          <input
            type="number"
            min={1}
            max={Math.max(1, pageCount)}
            value={ready ? state.currentPage + 1 : ''}
            disabled={!ready}
            onChange={(e) => {
              const n = Number(e.target.value);
              if (Number.isFinite(n) && n >= 1 && n <= pageCount) onGoToPage(n - 1);
            }}
            aria-label="Số trang hiện tại"
          />
          <span className="pageTotal">/ {pageCount || '—'}</span>
        </label>
        <button
          type="button"
          className="iconButton"
          disabled={!ready || state.currentPage >= pageCount - 1}
          onClick={() => onGoToPage(state.currentPage + 1)}
          title="Trang sau"
        >
          ↓
        </button>
      </div>

      <div className="toolbarDivider" />

      {/*
        BANG THONG TIN - thay cho cot phai co dinh cu.

        Cot cu chiem 320px vinh vien cho thong tin chi thinh thoang moi can. Nut
        nay tra cho khung xem chung 320px do va van giu nguyen duong den moi thu
        trong cot: danh sach doi tuong, doi thu tu chong lop, xoa, va bang chan
        doan font.
      */}
      <div className="toolbarGroup">
        <button
          type="button"
          {...{ [INSPECTOR_TRIGGER_ATTR]: '' }}
          className={`button small${state.inspectorOpen ? ' active' : ''}`}
          aria-pressed={state.inspectorOpen}
          aria-haspopup="dialog"
          onClick={() => store.toggleInspector()}
          title={
            state.inspectorOpen
              ? 'Đóng bảng thông tin (I hoặc Esc)'
              : 'Đối tượng trên trang và chẩn đoán khối chữ (I)'
          }
        >
          <span aria-hidden="true">ⓘ</span> Thông tin
          {annotationCount > 0 && (
            <span className="editBadge" aria-label={`${annotationCount} đối tượng`}>
              {annotationCount}
            </span>
          )}
        </button>
      </div>

      <div className="toolbarDivider" />

      {/*
        NUT LƯU O NGOAI CUNG BEN PHAI, theo yeu cau nguoi dung. Day la hanh dong
        cuoi cung cua ca luong (mo -> sua -> luu), nen dat o cuoi thanh cong cu
        cho dung mach doc trai-sang-phai.

        SO THAY DOI DANG CHO = sua chu CONG chu thich. Truoc day chi dem lan sua
        chu, nen dan mot ghi chu roi ro vao nut Luu van doc "chưa có thay đổi
        nào" - dem thieu mot nguon la mot loi bao SAI, khong phai trang tri.
      */}
      <div className="toolbarGroup toolbarSave">
        <button
          type="button"
          className={`button${pendingChanges > 0 ? ' primary' : ''}`}
          disabled={!ready || !writeCapable || saving}
          onClick={onSave}
          title={
            !writeCapable
              ? 'Lõi hiện tại chỉ đọc — không xuất được tệp'
              : pendingChanges > 0
                ? `Xuất tệp PDF với ${changeSummary} (Ctrl+S)`
                : 'Xuất tệp PDF (chưa có thay đổi nào) — Ctrl+S'
          }
        >
          {saving ? 'Đang xuất…' : 'Lưu'}
          {pendingChanges > 0 && !saving && (
            <span className="editBadge" aria-label={`${pendingChanges} thay đổi`}>
              {pendingChanges}
            </span>
          )}
        </button>
      </div>

      {/*
        Zoom da chuyen xuong thanh trang thai duoi cung (kieu Word). Chuc nang
        "Vừa rộng" (fit-width) da bo han theo yeu cau nguoi dung - khong con
        state, khong con nut.
      */}
    </header>
  );
}

function ToolButton({
  tool,
  ready,
  writeCapable,
}: {
  tool: ToolSpec;
  ready: boolean;
  writeCapable: boolean;
}) {
  const state = useAppState();
  const active = state.tool === tool.id;
  const blocked = tool.id === 'text' && !writeCapable;
  return (
    <button
      type="button"
      className={`toolButton${active ? ' active' : ''}`}
      aria-pressed={active}
      disabled={!ready || blocked}
      title={blocked ? 'Sửa chữ cần lõi có đường ghi — lõi hiện tại chỉ đọc' : hintWithShortcut(tool)}
      onClick={() => {
        store.setTool(tool.id);
        // "Sửa chữ" MO NGAY bang Thong tin: bo dieu khien phong / co chu / kieu
        // chu nam trong do, nen bat cong cu ma khong thay bang la nhu tinh nang
        // khong co tac dung. Mo thang o day (khong chi dua vao effect doi-tool
        // trong App) de ke ca khi DANG o che do 'text' roi bam lai van hien bang -
        // truong hop do khong co "chuyen tool" nen effect khong bat.
        if (tool.id === 'text') store.setInspectorOpen(true);
      }}
    >
      <span className="toolIcon" aria-hidden="true">{tool.icon}</span>
      <span className="toolLabel">{tool.label}</span>
    </button>
  );
}
